import type { Address } from 'viem'

/**
 * The exchanges a pool can belong to, and the ONE router that trades a pool of each. They are the eight factories the competition contract accepts
 * (views/TradingCompetitionV3/config.ts DEXES); a unit test fails when one of them has no entry here.
 *
 * Every factory, router, quoter and selector below was read from BNB Smart Chain on 2026-09-30 (`router.factory()` equals the factory, the
 * bytecode holds the selectors this code encodes; evidence/stage-token-pages-20260930/chain-verification-20260930.txt). None of it is a guess.
 *
 * No exchange is preferred: nothing in this module or its callers ranks, labels or defaults by exchange.
 */
export type RouterStyle =
  /** PancakeSwap V3 SwapRouter (v3-periphery): exactInputSingle with `deadline` inside the struct, multicall(bytes[]), recipients are plain addresses. */
  | 'pcs-v3'
  /** SwapRouter02 family (CADINUdex Smart Router, Uniswap V3 SwapRouter02): exactInputSingle without deadline, multicall(deadline, bytes[]), ADDRESS_THIS custody. */
  | 'router02-v3'
  /** UniswapV2Router02 family: swapExactTokensForTokens / swapExactETHForTokens / swapExactTokensForETH with a deadline. */
  | 'v2'
  /** BakerySwap: the same shapes with BNB in the function names. */
  | 'v2-bakery'

export interface PoolDex {
  name: string
  kind: 'v2' | 'v3'
  factory: Address
  router: Address
  style: RouterStyle
  /** QuoterV2 of the same exchange (V3 only). */
  quoter?: Address
  /** LP fee in basis points for V2 pairs (informational: the price-impact figure removes it). V3 reads the pool's own fee. */
  v2FeeBps?: number
}

export const WBNB: Address = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'

export const POOL_DEXES: readonly PoolDex[] = [
  { name: 'CADINUdex V3', kind: 'v3', factory: '0x2cb62aEBa8eB609020129983A5141DB883abca85', router: '0x67E0Ff806c1a76c37b62365714fE17A1261568bc', style: 'router02-v3', quoter: '0x2F3b82562468C016176418e1EA0b56Cd317b836a' },
  { name: 'PancakeSwap V3', kind: 'v3', factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865', router: '0x1b81D678ffb9C0263b24A97847620C99d213eB14', style: 'pcs-v3', quoter: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997' },
  { name: 'Uniswap V3', kind: 'v3', factory: '0xdB1d10011AD0Ff90774D0C6Bb92e5C5c8b4461F7', router: '0xB971eF87ede563556b2ED4b1C0b0019111Dd85d2', style: 'router02-v3', quoter: '0x78D78E420Da98ad378D7799bE8f4AF69033EB077' },
  { name: 'CADINUdex V2', kind: 'v2', factory: '0x5DaF6fEd0E85218B275DB7D86001621043b561DA', router: '0xeeCAd93313BeBA1EE187f52D4036fB5fc828Ac4E', style: 'v2', v2FeeBps: 25 },
  { name: 'PancakeSwap V2', kind: 'v2', factory: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73', router: '0x10ED43C718714eb63d5aA57B78B54704E256024E', style: 'v2', v2FeeBps: 25 },
  { name: 'Biswap', kind: 'v2', factory: '0x858E3312ed3A876947EA49d572A7C42DE08af7EE', router: '0x3a6d8cA21D1CF76F653A67577FA0D27453350dD8', style: 'v2', v2FeeBps: 10 },
  { name: 'BakerySwap', kind: 'v2', factory: '0x01bF7C66c6BD861915CdaaE475042d3c4BaE16A7', router: '0xCDe540d7eAFE93aC5fE6233Bee57E1270D3E330F', style: 'v2-bakery', v2FeeBps: 30 },
  { name: 'Uniswap V2', kind: 'v2', factory: '0x8909Dc15e40173Ff4699343b6eB8132c65e18eC6', router: '0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24', style: 'v2', v2FeeBps: 30 },
]

export const lc = (a: string) => a.toLowerCase()

export const dexOfFactory = (factory?: string | null): PoolDex | undefined =>
  factory ? POOL_DEXES.find((d) => lc(d.factory) === lc(factory)) : undefined

/** Tokens that take a fee on transfer break "amount sent = amount received"; the standard Swap page warns about them and so do we (same list as MultiSwap). */
export { FEE_ON_TRANSFER_TOKENS } from 'multiswap/config'
